import { useMemo, useState } from 'react'
import { Briefcase, MapPin, Clock, Mail, Award, Users, Sparkles, CheckCircle2, Send } from 'lucide-react'
import { useRecruitPositions } from '@/hooks/useData'

const emptyArray: never[] = []

export default function Careers() {
  const { data: positions = [] } = useRecruitPositions()
  const [activeDepartment, setActiveDepartment] = useState<string>('全部岗位')
  const [activeLocation, setActiveLocation] = useState<string>('全部地区')

  const departments = useMemo(() => {
    if (!positions.length) return ['全部岗位']
    const set = new Set<string>(['全部岗位'])
    positions.forEach((position) => set.add(position.department))
    return Array.from(set)
  }, [positions])

  const locations = useMemo(() => {
    if (!positions.length) return ['全部地区']
    const set = new Set<string>(['全部地区'])
    positions.forEach((position) => set.add(position.location))
    return Array.from(set)
  }, [positions])

  const filteredPositions = useMemo(() => {
    if (!positions.length) return emptyArray
    return positions.filter((position) => {
      const matchDepartment = activeDepartment === '全部岗位' || position.department === activeDepartment
      const matchLocation = activeLocation === '全部地区' || position.location === activeLocation
      return matchDepartment && matchLocation
    })
  }, [positions, activeDepartment, activeLocation])

  return (
    <div className="min-h-screen bg-white">
      <section className="py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white border border-secondary-200 rounded-2xl shadow-sm">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between p-6 border-b border-secondary-200">
              <div className="flex flex-wrap gap-2">
                {departments.map((department) => (
                  <button
                    key={department}
                    onClick={() => setActiveDepartment(department)}
                    className={`px-4 py-2 rounded-full border text-sm font-semibold transition-colors ${
                      activeDepartment === department
                        ? 'bg-primary-600 text-white border-primary-600 shadow-sm'
                        : 'border-secondary-200 text-secondary-600 hover:border-primary-400 hover:text-primary-600'
                    }`}
                  >
                    {department}
                  </button>
                ))}
              </div>
              <div className="flex flex-wrap gap-2">
                {locations.map((location) => (
                  <button
                    key={location}
                    onClick={() => setActiveLocation(location)}
                    className={`px-4 py-2 rounded-full border text-sm font-semibold transition-colors ${
                      activeLocation === location
                        ? 'bg-secondary-800 text-white border-secondary-800 shadow-sm'
                        : 'border-secondary-200 text-secondary-600 hover:border-secondary-400 hover:text-secondary-800'
                    }`}
                  >
                    {location}
                  </button>
                ))}
              </div>
            </div>

            <div className="divide-y divide-secondary-100">
              {filteredPositions.length === 0 ? (
                <div className="p-10 text-center text-secondary-500 text-sm">
                  当前暂无符合条件的岗位，欢迎关注后续招聘信息。
                </div>
              ) : (
                filteredPositions.map((position) => (
                  <div key={position.id} className="p-6 hover:bg-secondary-50/70 transition-colors">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                      <div>
                        <div className="flex flex-wrap items-center gap-3">
                          <h3 className="text-xl font-semibold text-secondary-900">{position.title}</h3>
                          <span className="inline-flex items-center gap-1 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-600">
                            {position.type}
                          </span>
                        </div>
                        <p className="mt-2 text-sm text-secondary-600 max-w-2xl leading-relaxed">{position.description}</p>
                        <div className="mt-3 flex flex-wrap gap-4 text-sm text-secondary-500">
                          <span className="inline-flex items-center gap-1"><MapPin className="h-4 w-4" /> {position.location}</span>
                          <span className="inline-flex items-center gap-1"><Clock className="h-4 w-4" /> {position.experience}</span>
                          <span className="inline-flex items-center gap-1"><Award className="h-4 w-4" /> {position.education}</span>
                          <span className="inline-flex items-center gap-1"><Users className="h-4 w-4" /> {position.department}</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-base font-semibold text-primary-700">{position.salary}</p>
                        <p className="text-xs text-secondary-400 mt-1">发布日期：{position.publishDate}</p>
                        <a
                          href="mailto:hnaedhb@163.com"
                          className="mt-4 inline-flex items-center gap-2 rounded-full border border-primary-200 px-4 py-2 text-sm font-semibold text-primary-600 hover:bg-primary-50"
                        >
                          <Send className="h-4 w-4" />
                          投递简历
                        </a>
                      </div>
                    </div>
                    <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6 text-sm text-secondary-600">
                      <div>
                        <h4 className="font-semibold text-secondary-800 mb-2">岗位职责</h4>
                        <ul className="space-y-2">
                          {(position.responsibilities ?? []).map((item, index) => (
                            <li key={`${position.id}-resp-${index}`} className="flex items-start gap-2">
                              <CheckCircle2 className="h-4 w-4 mt-1 text-primary-500 flex-shrink-0" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-semibold text-secondary-800 mb-2">任职要求</h4>
                        <ul className="space-y-2">
                          {(position.requirements ?? []).map((item, index) => (
                            <li key={`${position.id}-req-${index}`} className="flex items-start gap-2">
                              <CheckCircle2 className="h-4 w-4 mt-1 text-primary-500 flex-shrink-0" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    {position.tags && position.tags.length > 0 && (
                      <div className="mt-4 flex flex-wrap gap-2">
                        {position.tags.map((tag, index) => (
                          <span key={`${position.id}-tag-${index}`} className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-600">
                            #{tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-secondary-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div className="bg-white border border-primary-100 rounded-2xl p-8 shadow-sm">
              <h2 className="text-xl font-semibold text-secondary-900">成长与福利</h2>
              <p className="mt-2 text-secondary-600 text-sm leading-relaxed">
                我们致力于为人才搭建多维度的发展通道，提供具有竞争力的激励机制与温暖的人文关怀。
              </p>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm text-secondary-600">
                {[
                  '五险一金及补充商业保险',
                  '年度体检与健康关怀计划',
                  '双导师制培养与专业培训',
                  '项目绩效奖金与专项激励',
                  '节日福利与团队建设活动',
                  '宽敞舒适的办公与实验环境',
                ].map((benefit, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 mt-1 text-primary-500 flex-shrink-0" />
                    <span>{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white border border-primary-100 rounded-2xl p-8 shadow-sm">
              <h2 className="text-xl font-semibold text-secondary-900">应聘流程</h2>
              <div className="mt-6 space-y-4">
                {[
                  { step: '01', title: '投递简历', desc: '发送简历及相关证明材料至 hnaedhb@163.com，邮件注明“应聘岗位+姓名”。' },
                  { step: '02', title: '简历筛选', desc: '人力资源部与用人部门将在5个工作日内完成简历筛选并反馈结果。' },
                  { step: '03', title: '综合面试', desc: '邀请符合条件的候选人参加专业面试与综合能力评估。' },
                  { step: '04', title: '录用入职', desc: '提供录用通知，完成体检、入职手续及导师分配。' },
                ].map((item) => (
                  <div key={item.step} className="flex items-start gap-4">
                    <div className="h-10 w-10 rounded-full bg-primary-100 text-primary-600 font-semibold flex items-center justify-center">{item.step}</div>
                    <div>
                      <h3 className="text-base font-semibold text-secondary-900">{item.title}</h3>
                      <p className="text-sm text-secondary-600 mt-1 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-6 rounded-xl border border-secondary-200 bg-secondary-50 px-4 py-3 text-sm text-secondary-600 flex items-center gap-3">
                <Mail className="h-5 w-5 text-primary-600" />
                <span>咨询邮箱：hnaedhb@163.com · 咨询热线：0371-67116867</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
